import {
  DeesElement,
  type TemplateResult,
  property,
  customElement,
  html,
  css,
  cssManager,
  state,
} from '@design.estate/dees-element';

import * as domtools from '@design.estate/dees-domtools';
import { DeesContextmenu } from '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import type { IActivityEntry, IActivityLogAPI } from '../../interfaces/appconfig.js';
import { demoFunc } from './dees-appui-activitylog.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

@customElement('dees-appui-activitylog')
export class DeesAppuiActivitylog extends DeesElement implements IActivityLogAPI {
  // STATIC
  public static demo = demoFunc;
  public static demoGroups = ['App UI'];

  // INSTANCE PROPERTIES
  @state()
  accessor entries: IActivityEntry[] = [];

  @state()
  accessor searchQuery: string = '';

  @state()
  accessor filterCriteria: { user?: string; type?: IActivityEntry['type'] } = {};

  // RxJS Subject for reactive updates
  public entries$ = new domtools.plugins.smartrx.rxjs.Subject<IActivityEntry[]>();

  /** Show a static live indicator only when the host has an active stream. */
  @property({ type: Boolean })
  accessor live = false;

  // STYLES
  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        --activitylog-footer-height: 44px;
        /* systemPurple event accent (dark default, bright override below) */
        --activitylog-purple: #bf5af2;
        /* CSS Variables aligned with secondary menu */
        --activitylog-bg: var(--dees-appui-chrome-bg);
        --activitylog-fg: var(--dees-color-text-secondary);
        --activitylog-fg-muted: var(--dees-color-text-muted);
        --activitylog-fg-active: var(--dees-color-text-primary);
        --activitylog-border: var(--dees-color-border-subtle);
        --activitylog-hover: var(--dees-color-hover);
        --activitylog-accent: var(--dees-color-text-warm);

        color: var(--activitylog-fg);
        position: relative;
        display: block;
        width: 100%;
        height: 100%;
        background: var(--activitylog-bg);
        font-family: var(--dees-font-family);
        border-left: 1px solid var(--activitylog-border);
        cursor: default;
        overflow: hidden;
      }

      :host([gobright]) {
        --activitylog-purple: #af52de;
      }

      .maincontainer {
        position: absolute;
        top: 0px;
        left: 0px;
        height: 100%;
        width: 100%;
        min-width: 0;
      }

      /* Entry count and explicit stream status share the pane heading row. */
      .topbar {
        position: absolute;
        top: 0px;
        height: var(--dees-appui-row-height);
        width: 100%;
        padding: 0 var(--dees-spacing-md);
        background: var(--activitylog-bg);
        display: flex;
        align-items: center;
        gap: 8px;
        box-sizing: border-box;
      }

      .topbar .heading {
        font-weight: 600;
        font-size: var(--dees-font-control-size);
        letter-spacing: -0.01em;
        color: var(--activitylog-fg-active);
      }

      .live-indicator {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: var(--dees-font-caption2-size);
        font-weight: 500;
        color: var(--activitylog-fg-muted);
      }

      .live-indicator .dot {
        width: 6px;
        height: 6px;
        background: var(--dees-color-accent-success);
        border-radius: 50%;
      }
      .entry-count { min-width: 18px; padding: 2px 5px; border-radius: var(--dees-radius-full); font: 500 11px var(--dees-font-family); font-variant-numeric: tabular-nums; text-align: center; color: var(--dees-color-badge-default-fg); background: var(--dees-color-badge-default-bg); }
      .topbar .close-button { margin-left: auto; }

      /* Activity container */
      .activityContainer {
        position: absolute;
        top: var(--dees-appui-row-height);
        bottom: var(--activitylog-footer-height);
        width: 100%;
        padding: var(--dees-spacing-sm) 0;
        overflow-y: auto;
        overscroll-behavior: contain;
        scrollbar-width: thin;
        scrollbar-color: var(--dees-color-border-strong) transparent;
      }

      .activityContainer::-webkit-scrollbar {
        width: 6px;
      }

      .activityContainer::-webkit-scrollbar-track {
        background: transparent;
      }

      .activityContainer::-webkit-scrollbar-thumb {
        background: var(--dees-color-border-strong);
        border-radius: 3px;
      }

      .activityContainer::-webkit-scrollbar-thumb:hover {
        background: var(--dees-color-text-muted);
      }

      .empty-state {
        font-size: var(--dees-font-footnote-size);
        text-align: center;
        padding: var(--dees-spacing-3xl) var(--dees-spacing-lg);
        color: var(--activitylog-fg-muted);
      }

      /* Quiet section labels match navigation group headings. */
      .date-separator {
        padding: var(--dees-spacing-md) var(--dees-spacing-md) var(--dees-spacing-xs);
        font-size: var(--dees-font-caption2-size);
        font-weight: 600;
        letter-spacing: normal;
        color: var(--activitylog-fg-muted);
        position: sticky;
        top: 0;
        z-index: 1;
        background: var(--activitylog-bg);
      }

      /* Activity entry - modern stacked layout */
      .activityentry {
        font-size: var(--dees-font-control-size);
        padding: 8px 12px;
        margin: 2px 4px;
        border-radius: var(--dees-radius-sm);
        transition: background var(--dees-transition-fast) var(--dees-ease-standard);
        display: flex;
        align-items: flex-start;
        gap: 12px;
        line-height: 1.4;
      }

      .activityentry:hover {
        background: var(--activitylog-hover);
      }

      .activity-icon {
        width: 24px;
        height: 24px;
        border-radius: var(--dees-radius-sm);
        background: var(--dees-color-hover);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        font-size: var(--dees-font-control-size-sm);
        color: var(--activitylog-fg-muted);
        margin-top: 1px;
      }

      .activity-icon.login {
        background: color-mix(in srgb, var(--dees-color-accent-success) 10%, transparent);
        color: var(--dees-color-text-success);
      }

      .activity-icon.logout {
        background: color-mix(in srgb, var(--dees-color-accent-error) 10%, transparent);
        color: var(--dees-color-text-error);
      }

      .activity-icon.view {
        background: color-mix(in srgb, var(--dees-color-accent-primary) 10%, transparent);
        color: var(--dees-color-accent-primary);
      }

      .activity-icon.create {
        background: color-mix(in srgb, var(--activitylog-purple) 10%, transparent);
        color: var(--activitylog-purple);
      }

      .activity-icon.update {
        background: color-mix(in srgb, var(--dees-color-accent-warning) 10%, transparent);
        color: var(--dees-color-accent-warning);
      }

      .activity-icon.delete {
        background: color-mix(in srgb, var(--dees-color-accent-error) 10%, transparent);
        color: var(--dees-color-text-error);
      }

      .activity-icon.custom {
        background: color-mix(in srgb, var(--dees-color-text-muted) 10%, transparent);
        color: var(--dees-color-text-muted);
      }

      .activity-content {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .activity-header {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .activity-user {
        font-weight: 600;
        font-size: var(--dees-font-control-size);
        color: var(--activitylog-fg-active);
      }

      .activity-separator {
        color: var(--activitylog-fg-muted);
        font-size: var(--dees-font-caption2-size);
      }

      .timestamp {
        color: var(--activitylog-fg-muted);
        font-weight: 400;
        font-size: var(--dees-font-caption2-size);
        font-variant-numeric: tabular-nums;
        font-family: var(--dees-font-family-mono);
      }

      .activity-message {
        color: var(--activitylog-fg);
        font-size: var(--dees-font-control-size);
        line-height: 1.5;
        word-break: break-word;
      }

      /* Search box - refined styling */
      .searchbox {
        position: absolute;
        bottom: 0px;
        width: 100%;
        height: var(--activitylog-footer-height);
        background: var(--activitylog-bg);
        padding: var(--dees-spacing-sm) var(--dees-spacing-md);
        box-sizing: border-box;
      }

      .search-wrapper {
        position: relative;
        width: 100%;
        height: 28px;
      }

      .close-button { display: grid; place-items: center; width: 28px; height: 28px; flex-shrink: 0; border: 0; border-radius: var(--dees-radius-md); background: transparent; color: var(--activitylog-fg); cursor: pointer; }
      .close-button:hover { background: var(--dees-color-hover); }
      .close-button:focus-visible { outline: 2px solid var(--dees-color-focus-ring); }
      @media (pointer: coarse) {
        :host { --activitylog-footer-height: 60px; }
        .close-button { width: 44px; height: 44px; }
        .searchbox { padding: 7px 12px; }
        .search-wrapper { height: 44px; }
      }

      .search-icon {
        position: absolute;
        left: 10px;
        top: 50%;
        transform: translateY(-50%);
        color: var(--activitylog-fg-muted);
        font-size: var(--dees-font-control-size-sm);
        pointer-events: none;
        transition: color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .searchbox input {
        color: var(--activitylog-fg-active);
        background: var(--dees-color-fill-tertiary);
        width: 100%;
        height: 100%;
        border: 1px solid transparent;
        border-radius: var(--dees-radius-md);
        corner-shape: var(--dees-corner-shape);
        padding: 0 12px 0 34px;
        font-family: var(--dees-font-family);
        font-size: var(--dees-font-caption1-size);
        transition: background var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .searchbox input::placeholder {
        color: var(--activitylog-fg-muted);
      }

      .searchbox input:focus {
        outline: none;
        border-color: var(--dees-color-accent-primary);
        box-shadow: 0 0 0 3px var(--dees-color-focus-ring);
        background: var(--dees-color-bg-primary);
      }

      .search-wrapper:has(input:focus) .search-icon {
        color: var(--activitylog-fg);
      }
    `,
  ];

  // RENDER
  public render(): TemplateResult {
    const filteredEntries = this.getFilteredEntries();
    const groupedEntries = this.groupEntriesByDate(filteredEntries);

    return html`
      ${domtools.elementBasic.styles}
      <style></style>
      <div class="maincontainer">
        <div class="topbar">
          <div class="heading">Activity</div>
          <span class="entry-count" title="Visible entries">${filteredEntries.length}</span>
          ${this.live
            ? html`<div class="live-indicator"><span class="dot"></span>Live</div>`
            : ''}
          <button type="button" class="close-button" aria-label="Close activity log" @click=${() => this.dispatchEvent(new CustomEvent('close-request', { bubbles: true, composed: true }))}><dees-icon icon="lucide:X" iconSize="16"></dees-icon></button>
        </div>
        <div class="activityContainer">
          ${filteredEntries.length === 0
            ? html`<div class="empty-state">No activity entries</div>`
            : groupedEntries.map(
                (group) => html`
                  <div class="date-separator">${group.label}</div>
                  ${group.entries.map((entry) => this.renderActivityEntry(entry))}
                `
              )}
        </div>
        <div class="searchbox">
          <div class="search-wrapper">
            <dees-icon class="search-icon" .icon=${'lucide:search'}></dees-icon>
            <input
              type="text"
              aria-label="Search activity"
              placeholder="Search activities, users..."
              .value=${this.searchQuery}
              @input=${this.handleSearchInput}
            />
          </div>
        </div>
      </div>
    `;
  }

  private renderActivityEntry(entry: IActivityEntry): TemplateResult {
    const timestamp = entry.timestamp || new Date();
    const timeStr = this.formatTime(timestamp);
    const iconName = entry.iconName || this.getIconForType(entry.type);

    return html`
      <div
        class="activityentry"
        @contextmenu=${(e: MouseEvent) => this.handleContextMenu(e, entry)}
      >
        <div class="activity-icon ${entry.type}">
          <dees-icon .icon=${iconName}></dees-icon>
        </div>
        <div class="activity-content">
          <div class="activity-header">
            <span class="activity-user">${entry.user}</span>
            <span class="activity-separator">·</span>
            <span class="timestamp">${timeStr}</span>
          </div>
          <div class="activity-message">${entry.message}</div>
        </div>
      </div>
    `;
  }

  // API METHODS
  public add(entry: IActivityEntry): void {
    const newEntry: IActivityEntry = {
      ...entry,
      id: entry.id || this.generateId(),
      timestamp: entry.timestamp || new Date(),
    };
    this.entries = [newEntry, ...this.entries];
    this.entries$.next(this.entries);
  }

  public addMany(entries: IActivityEntry[]): void {
    const newEntries = entries.map((entry) => ({
      ...entry,
      id: entry.id || this.generateId(),
      timestamp: entry.timestamp || new Date(),
    }));
    this.entries = [...newEntries.reverse(), ...this.entries];
    this.entries$.next(this.entries);
  }

  public clear(): void {
    this.entries = [];
    this.entries$.next(this.entries);
  }

  public getEntries(): IActivityEntry[] {
    return [...this.entries];
  }

  public filter(criteria: { user?: string; type?: IActivityEntry['type'] }): IActivityEntry[] {
    return this.entries.filter((entry) => {
      if (criteria.user && entry.user !== criteria.user) return false;
      if (criteria.type && entry.type !== criteria.type) return false;
      return true;
    });
  }

  public search(query: string): IActivityEntry[] {
    const lowerQuery = query.toLowerCase();
    return this.entries.filter(
      (entry) =>
        entry.message.toLowerCase().includes(lowerQuery) ||
        entry.user.toLowerCase().includes(lowerQuery)
    );
  }

  // PRIVATE HELPERS
  private generateId(): string {
    return `activity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private getFilteredEntries(): IActivityEntry[] {
    let result = this.entries;

    if (this.searchQuery) {
      const lowerQuery = this.searchQuery.toLowerCase();
      result = result.filter(
        (entry) =>
          entry.message.toLowerCase().includes(lowerQuery) ||
          entry.user.toLowerCase().includes(lowerQuery)
      );
    }

    if (this.filterCriteria.user || this.filterCriteria.type) {
      result = result.filter((entry) => {
        if (this.filterCriteria.user && entry.user !== this.filterCriteria.user) return false;
        if (this.filterCriteria.type && entry.type !== this.filterCriteria.type) return false;
        return true;
      });
    }

    return result;
  }

  private groupEntriesByDate(
    entries: IActivityEntry[]
  ): Array<{ label: string; entries: IActivityEntry[] }> {
    const groups: Map<string, IActivityEntry[]> = new Map();
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    for (const entry of entries) {
      const date = entry.timestamp || new Date();
      let label: string;

      if (this.isSameDay(date, today)) {
        label = 'Today';
      } else if (this.isSameDay(date, yesterday)) {
        label = 'Yesterday';
      } else {
        label = date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
        });
      }

      if (!groups.has(label)) {
        groups.set(label, []);
      }
      groups.get(label)!.push(entry);
    }

    return Array.from(groups.entries()).map(([label, entries]) => ({
      label,
      entries,
    }));
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  private formatTime(date: Date): string {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  private getIconForType(type: IActivityEntry['type']): string {
    const icons: Record<IActivityEntry['type'], string> = {
      login: 'lucide:logIn',
      logout: 'lucide:logOut',
      view: 'lucide:eye',
      create: 'lucide:plus',
      update: 'lucide:edit',
      delete: 'lucide:trash2',
      custom: 'lucide:activity',
    };
    return icons[type] || icons.custom;
  }

  private handleSearchInput(e: InputEvent): void {
    const target = e.target as HTMLInputElement;
    this.searchQuery = target.value;
  }

  private handleContextMenu(e: MouseEvent, entry: IActivityEntry): void {
    e.preventDefault();
    DeesContextmenu.openContextMenuWithOptions(e, [
      {
        name: 'Copy activity',
        iconName: 'lucide:copy',
        action: async () => {
          await navigator.clipboard.writeText(`${entry.user} ${entry.message}`);
        },
      },
      {
        name: 'Filter by user',
        iconName: 'lucide:user',
        action: async () => {
          this.filterCriteria = { user: entry.user };
        },
      },
      {
        name: 'Filter by type',
        iconName: 'lucide:filter',
        action: async () => {
          this.filterCriteria = { type: entry.type };
        },
      },
      {
        name: 'Clear filters',
        iconName: 'lucide:x',
        action: async () => {
          this.filterCriteria = {};
          this.searchQuery = '';
        },
      },
    ]);
  }
}
